<template>
  <div class="flex flex-col gap-4" v-if="!isLoading">
    <div class="flex flex-col gap-2">
      <h1 class="font-extrabold text-2xl">{{ t("eventTypes.title") }}</h1>
      <div class="text-light-gray text-sm">
        {{ t("eventTypes.subTitle") }}
      </div>
    </div>

    <CustomTable
      :data="defaultEventTypes"
      :is-loading="isLoadingTable"
      :total-entries="totalEntries"
      :columns="[
        {
          key: 'eventType',
          title: t('eventTypes.table.eventType'),
          sortable: false,
        },
        {
          key: 'typicalDurationDays',
          title: t('eventTypes.table.typicalDurationDays'),
          sortable: false,
        },
        {
          key: 'defaultResources',
          title: t('eventTypes.table.defaultResources'),
          sortable: true,
          render: (resources: DefaultResource[]) =>
            h(
              'div',
              { class: 'flex flex-row gap-1 flex-wrap' },
              resources.map((resource) =>
                h(
                  'span',
                  {
                    key: resource.resourceType,
                    class: 'rounded-xl px-2 text-primary bg-secondary',
                  },
                  resource.resourceType,
                ),
              ),
            ),
        },
        {
          key: 'actions',
          title: t('events.list.table.actions'),
          sortable: false,
          render: (_, row: DefaultResource) =>
            h(
              'div',
              {
                class: 'flex flex-row gap-2 items-center ',
              },
              [
                h(
                  CustomButton,
                  {
                    class: 'bg-secondary! text-primary border-none! p-2!',
                    onClick: () => {
                      selectedUser = row;
                      isDialogOpen = true;
                    },
                  },
                  () =>
                    h(IconManager, {
                      name: 'Edit',
                    }),
                ),
              ],
            ),
        },
      ]"
    />
    <CustomDialog
      v-model="isDialogOpen"
      :title="t('users.dialog.title')"
      :is-submitting="isSubmitting"
      @submit="createDefaultResource"
      @cancel="isDialogOpen = false"
      :submitText="t('users.dialog.saveProfiles')"
    >
    </CustomDialog>
  </div>
</template>

<script setup lang="ts">
import { useToast } from "@/core/composables/use-toast";
import IconManager from "@/core/IconManager.vue";
import CustomDialog from "@/core/components/CustomDialog.vue";
import CustomButton from "@/core/components/CustomButton.vue";

//Table
import CustomTable from "@/core/components/table/CustomTable.vue";
import { useI18n } from "vue-i18n";
import { eventTypeRemoteDataSource } from "@/core/models/global-variables";
import { h, onMounted, ref } from "vue";
import { DefaultEventType } from "../models/default-event-type";
import { DefaultResource } from "../models/default-resource";

const { t } = useI18n();
const { showError } = useToast();

const defaultEventTypes = ref<DefaultEventType[]>([]);

const selectedUser = ref<DefaultResource | null>(null);

const isLoading = ref<boolean>(true);
const isLoadingTable = ref<boolean>(false);

onMounted(() => {
  loadInitialData();
});

//TableState
const totalEntries = ref(0);

//Dialog
const isDialogOpen = ref(false);
const isSubmitting = ref(false);

async function loadInitialData() {
  isLoading.value = true;

  try {
    const response = await eventTypeRemoteDataSource.getEventTypes();
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    }
    defaultEventTypes.value = response.data;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoading.value = false;
  }
}

async function createDefaultResource() {
  isLoading.value = true;

  try {
    const response = await eventTypeRemoteDataSource.getEventTypes();
    if (!response.isSuccess) {
      showError({
        message: response.detail!,
        title: response.title,
        statusCode: response.statusCode,
      });
    }
    defaultEventTypes.value = response.data;
  } catch (error: any) {
    showError({ title: error });
  } finally {
    isLoading.value = false;
  }
}
</script>
